import { useEffect, useState } from "react";
import { FaCheck, FaEdit, FaTimes, FaTrash } from "react-icons/fa";
import Moment from "react-moment";
import { toast } from "react-toastify";
import AddToMinyan from "../../../+global/modals/AddToMinyan";
import Delete from "../../../../helpers/ajax/delete";
import Put from "../../../../helpers/ajax/put";
import useConfirm from "../../../../hooks/useConfirm";

const ColonyUser = (props) => {
    const { user, getColony, colony, colony_id, ShowMinyanStatus } = props;
    const { confirm } = useConfirm();

    // Units the user holds in THIS colony (from units[] or legacy unit field)
    const colonyUnits = (user.units || []).filter((u) => u.colony_id === colony_id);
    const unitsForThisColony = colonyUnits.length ? colonyUnits : [{ _id: null, unit: user.unit }];

    const [EditUnitIndex, setEditUnitIndex] = useState(null); // index into unitsForThisColony
    const [EditUnitValue, setEditUnitValue] = useState("");
    const [Phone, setPhone] = useState(user.phone);
    const [EditPhone, setEditPhone] = useState(false);
    // Per-colony notification settings: prefer the matching settings[] entry
    const colonySetting = (user.settings || []).find((s) => s.colony_id === colony_id);
    const currentNotifications = colonySetting?.notifications ?? user.notifications;
    const currentNotificationMethod = colonySetting?.notificationMethod ?? user.notificationMethod;
    const [notifications, setNotifications] = useState(currentNotifications);
    const [notificationMethod, setNotificationMethod] = useState(currentNotificationMethod);
    // The backend can change notification prefs out from under us (e.g.
    // deleting the phone number flips "T" to "E"). Re-sync the local
    // dropdown state whenever the incoming user data changes so no stale
    // "Update" button appears for a change the backend already made.
    useEffect(() => {
        setNotifications(currentNotifications);
        setNotificationMethod(currentNotificationMethod);
        setPhone(user.phone);
    }, [currentNotifications, currentNotificationMethod, user.phone]);
    const [BypassCode, setBypassCode] = useState(user?.bypassCode);
    const [EditBypassCode, setEditBypassCode] = useState(false);
    const [AddToMinyanOpen, setAddToMinyanOpen] = useState(false);

    const deleteUser = async () => {
        const confirmDelete = await confirm({
            text: `Are you sure you want to delete ${user.first_name} ${user.last_name}? <br/> (this will remove all their data)`,
            confirmText: "Delete",
            title: "Delete User",
        });
        if (confirmDelete) {
            try {
                await Delete(`/api/users/${user._id}`);
                toast("User deleted!");
                getColony();
            } catch (error) {
                toast.error(error.error || "An error occurred while deleting the user");
            }
        }
    };

    const UpdateUser = async (updateField, updateValue, extra = {}) => {
        try {
            const result = await Put(`/api/users/${user._id}`, {
                updateField: updateField,
                updateValue: updateValue,
                ...extra,
            });
            // If the backend just deleted the phone number, flip the local
            // method dropdown to Email right away so no stale "Update" button
            // appears before the colony refresh lands.
            if (result?.phoneRemoved && notificationMethod === "T") {
                setNotificationMethod("E");
            }
            toast(
                <div>
                    User's {updateField.replace(/notificationMethod/g, "Notification Method")
                        .replace(/bypassCode/g, "Bypass Code")} updated to "{(updateField === "phone" && String(updateValue).trim() === "")
                            ? "Removed"
                            : String(updateValue).replace(/^E$/g, "Email")
                            .replace(/^T$/g, "Text")
                            .replace(/^N$/g, "Off")
                            .replace(/^A$/g, "Every Sign Up")
                            .replace(/^M$/g, "Once There's Minyan")}"
                    {updateField === "bypassCode" && (
                        <text className="text-red-600 text-lg font-bold">
                            <br />
                            Bypass Code expires in 24 hours
                        </text>
                    )}
                </div>
            );
            getColony();
        } catch (error) {
            toast.error(error.error || "An error occurred while updating the user");
        }
    };

    const DeleteRecord = async (recId) => {
        try {
            await Delete(`/api/minyan/${recId}`);
            toast("Removed from Minyan");
            getColony();
        } catch (error) {
            toast.error(error.error || "An error occurred while removing the minyan record");
        }
    };

    // Notification update: send colony_id so backend updates the right
    // user+colony settings entry.
    const updateNotifications = () => {
        if (notifications !== currentNotifications)
            UpdateUser("notifications", notifications, { colony_id });
        if (notificationMethod !== currentNotificationMethod)
            UpdateUser("notificationMethod", notificationMethod, { colony_id });
    };

    const deleteUnit = async (index) => {
        const unit = unitsForThisColony[index];
        const isLast = unitsForThisColony.length <= 1;
        const confirmDelete = await confirm({
            text: isLast
                ? `This is ${user.first_name}'s only unit. Deleting it removes the entire user account. Continue?`
                : `Remove unit ${unit.unit} from ${user.first_name} ${user.last_name}?`,
            confirmText: isLast ? "Delete User" : "Remove Unit",
            title: isLast ? "Delete User" : "Remove Unit",
        });
        if (confirmDelete) {
            try {
                if (unit._id) {
                    await Delete(`/api/users/${user._id}/units/${unit._id}`);
                } else {
                    // Legacy unit (no _id) - just delete the whole user
                    await Delete(`/api/users/${user._id}`);
                }
                toast(isLast ? "User deleted!" : "Unit removed");
                getColony();
            } catch (error) {
                toast.error(error.error || "An error occurred while removing the unit");
            }
        }
    };

    return (
        <div className="rounded-lg shadow-lg border p-3 relative">
            <button
                onClick={deleteUser}
                className="absolute bg-white text-lg p-2 border rounded ring-black/30 focus:ring-2 ring-offset-2 focus:outline-none top-2 right-2"
            >
                <FaTrash className="text-dependable " />
            </button>
            <div>
                <table className="border-separate">
                    <tbody>
                        <tr>
                            <td className="pr-2 font-light">Name:</td>
                            <td>
                                {user.first_name} {user.last_name}
                            </td>
                        </tr>
                        <tr>
                            <td className="pr-2 font-light">Email:</td>
                            <td>{user.email}</td>
                        </tr>
                        {!!user.username && (
                            <tr>
                                <td className="pr-2 font-light">Username:</td>
                                <td>{user.username}</td>
                            </tr>
                        )}
                        <tr>
                            <td className="pr-2 font-light">Phone:</td>
                            <td>
                                {!EditPhone && (
                                    <>
                                        {user.phone}
                                        <button
                                            onClick={() => {
                                                setEditPhone(true);
                                            }}
                                            className="ml-2 align-middle"
                                        >
                                            <FaEdit className="text-dependable" />
                                        </button>
                                    </>
                                )}
                                {EditPhone && (
                                    <div className="relative ">
                                        <input
                                            autoFocus
                                            className="w-full block rounded-md border focus:outline-none focus:ring-2 ring-offset-2 ring-black/30 px-2 py-1"
                                            type="text"
                                            value={Phone ? Phone : ""}
                                            onChange={(e) => setPhone(e.target.value)}
                                        />
                                        <button
                                            onClick={() => {
                                                setPhone(user.phone);
                                                setEditPhone(false);
                                            }}
                                            className="absolute text-lg p-1 focus:outline-none top-1 right-0"
                                        >
                                            <FaTimes className="text-dependable " />
                                        </button>
                                        <button
                                            onClick={() => {
                                                const trimmed = (Phone || "").trim();
                                                if (trimmed === "") {
                                                    // Emptying the field deletes the number.
                                                    UpdateUser("phone", "");
                                                    setEditPhone(false);
                                                    return;
                                                }
                                                let phone = Phone.replace(/\D/g, "");
                                                if (phone.length !== 10) {
                                                    toast.error("Invalid phone number");
                                                    return;
                                                }
                                                UpdateUser("phone", phone);
                                                setEditPhone(false);
                                            }}
                                            className="absolute text-lg p-1 focus:outline-none top-1 right-6 disabled:opacity-50"
                                        >
                                            <FaCheck className="text-dependable " />
                                        </button>
                                    </div>
                                )}
                            </td>
                        </tr>
                        {/* Unit rows: one per unit in this colony */}
                        {unitsForThisColony.map((unit, idx) => (
                            <tr key={unit._id || `legacy-${idx}`}>
                                <td className="pr-2 font-light">{unitsForThisColony.length > 1 ? `Unit (${idx + 1}):` : "Unit:"}</td>
                                <td>
                                    {EditUnitIndex !== idx ? (
                                        <>
                                            {unit.unit ?? user.unit}
                                            <button
                                                onClick={() => {
                                                    setEditUnitIndex(idx);
                                                    setEditUnitValue(unit.unit ?? user.unit);
                                                }}
                                                className="ml-2 align-middle"
                                            >
                                                <FaEdit className="text-dependable" />
                                            </button>
                                            <button onClick={() => deleteUnit(idx)} className="ml-2 align-middle">
                                                <FaTrash className="text-red-600" />
                                            </button>
                                        </>
                                    ) : (
                                        <div className="relative">
                                            <input
                                                autoFocus
                                                className="w-full block rounded-md border focus:outline-none focus:ring-2 ring-offset-2 ring-black/30 px-2 py-1"
                                                type="text"
                                                value={EditUnitValue}
                                                onChange={(e) => setEditUnitValue(e.target.value)}
                                            />
                                            <button
                                                onClick={() => {
                                                    setEditUnitIndex(null);
                                                    setEditUnitValue("");
                                                }}
                                                className="absolute text-lg p-1 focus:outline-none top-1 right-0"
                                            >
                                                <FaTimes className="text-dependable " />
                                            </button>
                                            <button
                                                disabled={!EditUnitValue}
                                                onClick={async () => {
                                                    try {
                                                        if (unit._id) {
                                                            await Put(`/api/users/${user._id}/units/${unit._id}`, { unit: EditUnitValue });
                                                        } else {
                                                            // Legacy single-unit user: update the legacy unit field
                                                            await Put(`/api/users/${user._id}`, { updateField: "unit", updateValue: EditUnitValue });
                                                        }
                                                        toast("Unit updated");
                                                        getColony();
                                                    } catch (error) {
                                                        toast.error(error.error || "An error occurred while updating the unit");
                                                    }
                                                    setEditUnitIndex(null);
                                                    setEditUnitValue("");
                                                }}
                                                className="absolute text-lg p-1 focus:outline-none top-1 right-6 disabled:opacity-50"
                                            >
                                                <FaCheck className="text-dependable " />
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td className="pr-2 font-light">Notification Trigger:</td>
                            <td className="">
                                <select className="border border-depenable  rounded-md w-44 bg-white text-sm p-1" value={notifications} onChange={(e) => setNotifications(e.target.value)}>
                                    <option value="N">Off</option>
                                    <option value="A">Every Sign Up</option>
                                    <option value="M">Once There's Minyan</option>
                                </select>
                            </td>
                            <td>
                                {(notifications !== currentNotifications || notificationMethod !== currentNotificationMethod) && (
                                    <button
                                        className="bg-dependable text-white p-2 rounded-md "
                                        onClick={updateNotifications}
                                    >
                                        Update
                                    </button>
                                )}
                            </td>
                        </tr>
                        {notifications !== "N" && (
                            <tr>
                                <td className="pr-2 font-light">Notification Method:</td>
                                <td>
                                    <select className="border border-depenable  rounded-md w-44 bg-white text-sm p-1" value={notificationMethod} onChange={(e) => setNotificationMethod(e.target.value)}>
                                        <option value="E">Email</option>
                                        <option value="T">Text</option>
                                    </select>
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td className="pr-2 font-light">Bypass Code:</td>
                            <td>
                                {!EditBypassCode && (
                                    <>
                                        {user?.bypassCode || "Not set"}
                                        <button onClick={() => setEditBypassCode(true)} className="ml-2 align-middle">
                                            <FaEdit className="text-dependable" />
                                        </button>
                                    </>
                                )}
                                {EditBypassCode && (
                                    <div className="relative">
                                        <input autoFocus onChange={(e) => setBypassCode(e.target.value)} className="w-full block rounded-md border focus:outline-none focus:ring-2 ring-offset-2 ring-black/30 px-2 py-1" type="text" value={BypassCode || ""} />
                                        <button onClick={() => { setBypassCode(user?.bypassCode); setEditBypassCode(false); }} className="absolute text-lg p-1 focus:outline-none top-1 right-0">
                                            <FaTimes className="text-dependable " />
                                        </button>
                                        <button disabled={!BypassCode} onClick={() => { UpdateUser("bypassCode", BypassCode); setEditBypassCode(false); }} className="absolute text-lg p-1 focus:outline-none top-1 right-6 disabled:opacity-50">
                                            <FaCheck className="text-dependable " />
                                        </button>
                                    </div>
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td className="pr-2 font-light">Role:</td>
                            <td>{user.admin ? "Admin" : "User"}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="grid grid-cols-12 gap-3 mt-3">
                <button
                    className="border-depenable text-depborder-depenable border rounded-full col-span-5 text-xs px-2 py-1"
                    onClick={() => {
                        UpdateUser("admin", !user.admin);
                    }}
                >
                    Make {user.admin ? "not " : ""}admin
                </button>
                <button
                    onClick={() => setAddToMinyanOpen(true)}
                    className="border-depenable text-depborder-depenable border rounded-full col-span-5 text-xs px-2 py-1"
                >
                    Add to minyan
                </button>
            </div>

            {ShowMinyanStatus && (
                <div className="pt-5">
                    {!!user.upcomingRecords.length && (
                        <>
                            <p className="mb-1 ">Upcoming Minyan Signups</p>
                            <table className="w-full table-auto">
                                <thead>
                                    <tr className="border-b-2">
                                        <th>Type</th>
                                        <th>Date</th>
                                        <th>People</th>
                                        {colony?.askMikvahOnSignup === true && <th>Mikvah</th>}
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {user.upcomingRecords.map((record) => {
                                        return (
                                            <tr key={record.date} className="border-b">
                                                <td>{record.date_title}</td>
                                                <td><Moment date={record.date} format=" MMMM Do" /></td>
                                                <td>{record.poeple_count}</td>
                                                {colony?.askMikvahOnSignup === true && <td>{record.useMikvah ? "Yes" : "No"}</td>}
                                                <td>
                                                    <button className="text-red-500" onClick={() => DeleteRecord(record._id)}>
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    )}
                    {!user.upcomingRecords.length && <p>No upcoming dates</p>}
                </div>
            )}

            {AddToMinyanOpen && (
                <AddToMinyan getColony={getColony} colony={colony} user={user} close={() => setAddToMinyanOpen(false)} />
            )}
        </div>
    );
};

export default ColonyUser;